import React, { useMemo, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { useTheme } from "@mui/material";

import { PageTitle } from "components/common/Text";
import { PageFooter } from "components/common/Wrapper";
import { PrimaryButton, GhostButton } from "components/common/Buttons";
import { createNewPool, deleteDevicePool } from "api/DevicePoolHelpers";
import { FormInput, FieldError } from "components/common/Input";
import { showSuccess, showError } from "utils/notifications";
import CustomTable from "components/table";
import { TableBodyCell } from "components/table/table.style";
import { useRequest } from "hooks/useRequest";
import useConfirm from "hooks/useConfirm";
import { getCurrentPage, getFilteredListNew, getSortList, nameValidationCheck } from "utils/util-functions";
import { useAuth } from "hooks/useProvideAuth";
import TableFilterNew from "components/table/TableFilterNew.component";
import POOLS_COLUMNS from "./Pools.config";
import style from "./pools.page.module.css";

const PoolsPage = () => {
  const [isOpenNewPoolModal, setIsOpenNewPoolModal] = useState(false);
  const [isCreatingNewPool, setIsCreatingNewPool] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterColumn, setFilterColumn] = useState();
  const [filterType, setFilterType] = useState();
  const [filterWord, setFilterWord] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [sortBy, setSortBy] = useState();

  const theme = useTheme();
  const [getConfirmation, Confirmation] = useConfirm();
  const { organizationName: organizationId } = useAuth();

  const filterColumns = useMemo(() => POOLS_COLUMNS.filter(({ filter }) => filter), [POOLS_COLUMNS]);

  const { data: poolList, isLoading, mutate } = useRequest(`devicePools`);

  const filteredList = useMemo(
    () => getFilteredListNew(poolList, filterColumn, filterType, filterWord),
    [poolList, filterColumn, filterWord, filterType],
  );

  const sortedList = useMemo(() => {
    const sortColumn = POOLS_COLUMNS.find(({ id }) => id === sortBy);
    return getSortList(filteredList, sortBy, sortOrder, sortColumn?.type);
  }, [sortBy, sortOrder, filteredList]);

  const paginatedList = useMemo(() => getCurrentPage(sortedList, page, rowsPerPage), [sortedList, page, rowsPerPage]);

  const totalPoolCount = useMemo(() => filteredList?.length || 0, [filteredList]);

  const bodyColumns = useMemo(() => {
    const tempCols = [...POOLS_COLUMNS];
    tempCols.pop();
    return tempCols;
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm();

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const onNewPool = () => {
    setIsOpenNewPoolModal(true);
    reset();
    clearErrors();
  };

  const closeNewPoolModal = () => {
    setIsOpenNewPoolModal(false);
  };

  const onDeletePool = async () => {
    setAnchorEl(null);

    const isConfirm = await getConfirmation({
      title: "Delete Pool",
      content: "Are you sure to delete the device pool?",
    });

    if (isConfirm) {
      try {
        await deleteDevicePool(currentRow.devicePool);

        mutate();
        showSuccess("Successfully deleted the device pool!");
      } catch (error) {
        // error handling
      }
    }
  };

  const handleRowClick = (poolId) => {
    if (poolId) {
      navigate(`/pools/${poolId}`);
    }
  };

  const onModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setIsOpenNewPoolModal(false);
    }
  };

  const onAddNewPool = async (poolData) => {
    const { devicePool } = poolData;
    if (devicePool && organizationId) {
      setIsCreatingNewPool(true);

      try {
        const res = await createNewPool(devicePool, organizationId);

        if (res.data) {
          showSuccess("Add new device pool successfully!");
          setIsOpenNewPoolModal(false);
          mutate();
        }
      } catch (error) {
        // error handling
      } finally {
        setIsCreatingNewPool(false);
      }
    } else {
      // show warning
      showError("Pool name or Organization ID is not valid!");
    }
  };

  const onClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setCurrentRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentRow(null);
  };

  const onSearch = (column, columnType, searchContent) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
  };

  const onResetFilter = () => {
    setFilterColumn();
    setFilterWord();
    setFilterType();
    setPage(0);
  };

  const handleRequestSort = (order, orderBy) => {
    setSortOrder(order);
    setSortBy(orderBy);
  };

  return (
    <>
      <PageTitle>Pools</PageTitle>

      <div className={style.barContainer}>
        <div className={style.filterContainer}>
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        </div>

        <div className={style.buttonOptions}>
          <NxtIconButton icon={<AddCircleOutlineIcon />} onClick={onNewPool} text="New Pool" />
        </div>
      </div>

      <CustomTable
        rowsPerPageOptions={[10, 25, 100]}
        totalCount={totalPoolCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(pageNum) => setPage(pageNum)}
        onRowsPerPageChange={(rowPerPage) => setRowsPerPage(rowPerPage)}
      >
        <CustomTable.Header columns={POOLS_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={paginatedList}>
          {paginatedList &&
            paginatedList.map((row) => (
              <TableRow hover key={row.devicePool}>
                {bodyColumns.map((column) => {
                  const value = row[column.id];
                  return (
                    <TableBodyCell key={column.id} onClick={() => handleRowClick(row.devicePool)}>
                      {value}
                    </TableBodyCell>
                  );
                })}
                <TableBodyCell style={{ width: 30 }}>
                  <Button onClick={(e) => onClickMenu(e, row)} style={{ color: theme.palette.common.black }}>
                    <MoreVertIcon />
                  </Button>
                </TableBodyCell>
              </TableRow>
            ))}
        </CustomTable.Body>
      </CustomTable>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={onDeletePool}>Delete</MenuItem>
      </Menu>

      <Dialog fullWidth maxWidth="xs" open={isOpenNewPoolModal} onClose={onModalClose} disableEscapeKeyDown>
        <DialogTitle>New Pool</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <FormInput
              {...register("devicePool", {
                required: "Pool Name is required!",
                validate: {
                  nameValidationCheck,
                },
              })}
              placeholder="Pool Name"
              error={errors.devicePool}
            />
            {errors.devicePool && <FieldError>{errors.devicePool.message}</FieldError>}

            <PageFooter style={{ paddingTop: "16px" }}>
              <PrimaryButton loading={isCreatingNewPool} onClick={handleSubmit(onAddNewPool)}>
                Save
              </PrimaryButton>
              <GhostButton onClick={() => closeNewPoolModal()} sx={{ ml: 2 }}>
                Cancel
              </GhostButton>
            </PageFooter>
          </Box>
        </DialogContent>
      </Dialog>

      <Confirmation />
    </>
  );
};

export default PoolsPage;
